import type {
  TerminalTicketSettings,
  TicketLayoutAlign,
  TicketLayoutElement,
  TicketLayoutElementType,
} from "../../../types/Admin/TerminalAppearanceTypes";

export const TICKET_PAPER_WIDTH_PX = 384;
export const TICKET_PAPER_HEIGHT_PX = 640;
export const TICKET_PAPER_MIN_HEIGHT_PX = 200;
export const TICKET_PAPER_MAX_HEIGHT_PX = 1600;

export const UNIQUE_TICKET_FIELD_TYPES: TicketLayoutElementType[] = [
  "COMPANY_NAME",
  "TICKET_NUMBER",
  "TOPIC_NAME",
  "FIRST_LINE",
  "SECOND_LINE",
  "REGISTRATION_DATE",
  "REGISTRATION_TIME",
  "REGISTRATION_DATETIME",
];

export const TICKET_TEXT_FIELD_TYPES: TicketLayoutElementType[] = [
  "COMPANY_NAME",
  "FIRST_LINE",
  "SECOND_LINE",
  "CUSTOM_TEXT",
];

export const TICKET_FIELD_LABELS: Record<TicketLayoutElementType, string> = {
  COMPANY_NAME: "Название компании",
  TICKET_NUMBER: "Номер талона",
  TOPIC_NAME: "Название услуги",
  FIRST_LINE: "Первая строка",
  SECOND_LINE: "Вторая строка",
  CUSTOM_TEXT: "Произвольный текст",
  REGISTRATION_DATE: "Дата регистрации",
  REGISTRATION_TIME: "Время регистрации",
  REGISTRATION_DATETIME: "Дата и время регистрации",
  IMAGE: "Изображение",
};

export const TICKET_PREVIEW_SAMPLES: Partial<Record<TicketLayoutElementType, string>> = {
  TICKET_NUMBER: "A139",
  TOPIC_NAME: "Консультация",
  REGISTRATION_DATE: formatPreviewDate(),
  REGISTRATION_TIME: formatPreviewTime(),
  REGISTRATION_DATETIME: `Время регистрации: ${formatPreviewDate()}    ${formatPreviewTime()}`,
};

const DEFAULT_ELEMENT_SIZE: Record<
  TicketLayoutElementType,
  { width: number; height: number; fontSize: number }
> = {
  COMPANY_NAME: { width: 90, height: 8, fontSize: 2 },
  TICKET_NUMBER: { width: 90, height: 16, fontSize: 8 },
  TOPIC_NAME: { width: 90, height: 10, fontSize: 3 },
  FIRST_LINE: { width: 90, height: 6, fontSize: 1 },
  SECOND_LINE: { width: 90, height: 6, fontSize: 1 },
  CUSTOM_TEXT: { width: 90, height: 6, fontSize: 1 },
  REGISTRATION_DATE: { width: 90, height: 6, fontSize: 1 },
  REGISTRATION_TIME: { width: 90, height: 6, fontSize: 1 },
  REGISTRATION_DATETIME: { width: 90, height: 6, fontSize: 1 },
  IMAGE: { width: 70, height: 18, fontSize: 1 },
};

export const createTicketElementId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `ticket-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

export const isUniqueTicketFieldType = (type: TicketLayoutElementType): boolean =>
  UNIQUE_TICKET_FIELD_TYPES.includes(type);

export const isTicketTextFieldType = (type: TicketLayoutElementType): boolean =>
  TICKET_TEXT_FIELD_TYPES.includes(type);

const DEFAULT_TEXT_BY_TYPE: Partial<Record<TicketLayoutElementType, string>> = {
  COMPANY_NAME: "Название компании",
  FIRST_LINE: "Спасибо, что выбрали нас.",
  SECOND_LINE: "Хорошего дня!",
  CUSTOM_TEXT: "Текст",
};

export const normalizeTicketLayoutElement = (
  element: TicketLayoutElement,
): TicketLayoutElement => {
  if (element.id === "first-line" || element.type === "FIRST_LINE") {
    return { ...element, id: element.id || "first-line", type: "FIRST_LINE" };
  }
  if (element.id === "second-line" || element.type === "SECOND_LINE") {
    return { ...element, id: element.id || "second-line", type: "SECOND_LINE" };
  }
  return {
    ...element,
    id: element.id || `${element.type}-${element.x}-${element.y}`,
  };
};

export const buildDefaultTicketLayout = (
  ticket: TerminalTicketSettings,
): TicketLayoutElement[] => [
  {
    id: "company-name",
    type: "COMPANY_NAME",
    x: 5,
    y: 2,
    width: 90,
    height: 8,
    fontSize: ticket.companyNameFontSize || 2,
    align: "center",
    text: ticket.companyName || "Название компании",
  },
  {
    id: "ticket-number",
    type: "TICKET_NUMBER",
    x: 5,
    y: 12,
    width: 90,
    height: 16,
    fontSize: ticket.ticketNumberFontSize || 8,
    align: "center",
  },
  {
    id: "topic-name",
    type: "TOPIC_NAME",
    x: 5,
    y: 30,
    width: 90,
    height: 10,
    fontSize: ticket.topicNameFontSize || 3,
    align: "center",
  },
  {
    id: "first-line",
    type: "FIRST_LINE",
    x: 5,
    y: 42,
    width: 90,
    height: 6,
    fontSize: ticket.firstLineFontSize || 1,
    align: "center",
    text: ticket.firstLine || DEFAULT_TEXT_BY_TYPE.FIRST_LINE,
  },
  {
    id: "second-line",
    type: "SECOND_LINE",
    x: 5,
    y: 50,
    width: 90,
    height: 6,
    fontSize: ticket.secondLineFontSize || 1,
    align: "center",
    text: ticket.secondLine || DEFAULT_TEXT_BY_TYPE.SECOND_LINE,
  },
  {
    id: "registration-datetime",
    type: "REGISTRATION_DATETIME",
    x: 5,
    y: 58,
    width: 90,
    height: 6,
    fontSize: 1,
    align: "center",
  },
];

export const ensureTicketLayout = (
  ticket: TerminalTicketSettings,
): TicketLayoutElement[] => {
  const layout =
    ticket.layout && ticket.layout.length > 0
      ? ticket.layout
      : buildDefaultTicketLayout(ticket);
  return layout.map(normalizeTicketLayoutElement);
};

export const createTicketLayoutElement = (
  type: TicketLayoutElementType,
  layout: TicketLayoutElement[],
  extra?: Partial<TicketLayoutElement>,
): TicketLayoutElement => {
  const size = DEFAULT_ELEMENT_SIZE[type];
  const nextY = layout.reduce((max, el) => Math.max(max, (el.y || 0) + (el.height || 0)), 2);
  const y = Math.min(nextY + 1, 100 - size.height);
  const { id: extraId, type: _ignoredType, ...extraRest } = extra ?? {};
  const stableId =
    type === "FIRST_LINE"
      ? "first-line"
      : type === "SECOND_LINE"
        ? "second-line"
        : undefined;
  return {
    x: 5,
    y,
    width: size.width,
    height: size.height,
    fontSize: size.fontSize,
    align: "center",
    text: isTicketTextFieldType(type)
      ? extra?.text ?? DEFAULT_TEXT_BY_TYPE[type]
      : extra?.text,
    pictureId: extra?.pictureId ?? null,
    ...extraRest,
    type,
    id: extraId ?? stableId ?? createTicketElementId(),
  };
};

export const syncLegacyTicketFields = (
  ticket: TerminalTicketSettings,
  layout: TicketLayoutElement[],
): TerminalTicketSettings => {
  const company = layout.find((el) => el.type === "COMPANY_NAME");
  const firstLine = layout.find((el) => el.type === "FIRST_LINE");
  const secondLine = layout.find((el) => el.type === "SECOND_LINE");
  const extraCustomTexts = layout.filter((el) => el.type === "CUSTOM_TEXT");
  const ticketNumber = layout.find((el) => el.type === "TICKET_NUMBER");
  const topic = layout.find((el) => el.type === "TOPIC_NAME");
  return {
    ...ticket,
    layout,
    companyName: company?.text ?? ticket.companyName,
    firstLine: firstLine?.text ?? extraCustomTexts[0]?.text ?? ticket.firstLine,
    secondLine: secondLine?.text ?? extraCustomTexts[1]?.text ?? extraCustomTexts[0]?.text ?? ticket.secondLine,
    companyNameFontSize: company?.fontSize ?? ticket.companyNameFontSize,
    firstLineFontSize: firstLine?.fontSize ?? extraCustomTexts[0]?.fontSize ?? ticket.firstLineFontSize,
    secondLineFontSize:
      secondLine?.fontSize ?? extraCustomTexts[1]?.fontSize ?? extraCustomTexts[0]?.fontSize ?? ticket.secondLineFontSize,
    ticketNumberFontSize: ticketNumber?.fontSize ?? ticket.ticketNumberFontSize,
    topicNameFontSize: topic?.fontSize ?? ticket.topicNameFontSize,
  };
};

export const getTicketElementPreviewText = (
  element: TicketLayoutElement,
): string => {
  if (element.type === "IMAGE") {
    return "";
  }
  if (isTicketTextFieldType(element.type)) {
    return element.text?.trim()
      ? element.text
      : DEFAULT_TEXT_BY_TYPE[element.type] || TICKET_FIELD_LABELS[element.type];
  }
  return TICKET_PREVIEW_SAMPLES[element.type] || TICKET_FIELD_LABELS[element.type];
};

export const ticketFontSizeToPx = (fontSize?: number): number => {
  const size = Math.min(8, Math.max(1, fontSize || 1));
  return 10 + size * 5;
};

export const percentToPx = (percent: number, total: number): number =>
  Math.round((percent / 100) * total);

export const pxToPercent = (px: number, total: number): number => {
  if (!total) return 0;
  return Math.round((px / total) * 1000) / 10;
};

export const clampTicketPaperHeightPx = (
  height: number,
  minHeight = TICKET_PAPER_MIN_HEIGHT_PX,
): number => {
  const rounded = Math.round(height);
  return Math.min(
    TICKET_PAPER_MAX_HEIGHT_PX,
    Math.max(minHeight, Number.isFinite(rounded) ? rounded : TICKET_PAPER_HEIGHT_PX),
  );
};

export const resolveTicketPaperHeightPx = (
  ticket?: Pick<TerminalTicketSettings, "paperHeightPx"> | null,
): number => {
  if (ticket?.paperHeightPx == null || !Number.isFinite(ticket.paperHeightPx)) {
    return TICKET_PAPER_HEIGHT_PX;
  }
  return clampTicketPaperHeightPx(ticket.paperHeightPx);
};

export const getTicketLayoutBottomPx = (
  layout: TicketLayoutElement[],
  paperHeightPx: number,
): number =>
  layout.reduce((max, element) => {
    const bottom =
      percentToPx(element.y, paperHeightPx) + percentToPx(element.height, paperHeightPx);
    return Math.max(max, bottom);
  }, 0);

export const remapTicketLayoutToPaperHeight = (
  layout: TicketLayoutElement[],
  fromHeightPx: number,
  toHeightPx: number,
): TicketLayoutElement[] => {
  if (!fromHeightPx || !toHeightPx || fromHeightPx === toHeightPx) {
    return layout;
  }
  const scale = fromHeightPx / toHeightPx;
  return layout.map((element) => {
    const height = Math.max(0.1, roundPercent(element.height * scale));
    const maxY = Math.max(0, 100 - height);
    const y = Math.min(maxY, Math.max(0, roundPercent(element.y * scale)));
    return { ...element, y, height };
  });
};

const roundPercent = (value: number): number => Math.round(value * 10) / 10;

export const normalizeTicketAlign = (align?: string): TicketLayoutAlign => {
  if (align === "left" || align === "right") {
    return align;
  }
  return "center";
};

function formatPreviewDate(): string {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = String(now.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
}

function formatPreviewTime(): string {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}
